import {
    TAB_PLACEMENT_BOTTOM,
    normalizeDockZone,
} from './constants.js';
import {
    buildLayoutFromPanels,
    buildLayoutFromRegionSubtrees,
    createPanelNode,
    createSplitNode,
    createTabsNode,
} from './layoutTree.js';

export const MAX_BLUEPRINT_DEPTH = 8;

/**
 * @param {object} node
 * @param {number} depth
 */
export function buildBlueprintNode(node, depth = 0) {
    if (!node || depth > MAX_BLUEPRINT_DEPTH) {
        if (depth > MAX_BLUEPRINT_DEPTH) {
            console.warn(
                'MultipanelDockLayout: blueprint max depth exceeded',
            );
        }
        return null;
    }

    if (node.type === 'panel') {
        return node.panelId ? createPanelNode(node.panelId) : null;
    }

    if (node.type === 'tabGroup') {
        const ids = (node.panelIds || []).filter(Boolean);
        if (!ids.length) return null;
        const options = {
            tabPlacement: node.tabPlacement || TAB_PLACEMENT_BOTTOM,
            title: node.title || null,
        };
        const tabs = createTabsNode(ids, node.activePanelId || ids[0], options);
        if (node.dynamic) {
            tabs.contentMode = 'dynamic';
            tabs.contentPanelId =
                node.contentPanelId ||
                `${node.groupId || tabs.id}__content`;
        }
        return tabs;
    }

    if (node.type === 'split') {
        const children = (node.children || [])
            .map((ch) => buildBlueprintNode(ch, depth + 1))
            .filter(Boolean);
        if (!children.length) return null;
        if (children.length === 1) return children[0];
        const orientation =
            node.orientation === 'vertical' ? 'vertical' : 'horizontal';
        const sizes =
            node.sizes?.length === children.length ? node.sizes : undefined;
        return createSplitNode(orientation, children, sizes);
    }

    return null;
}

/**
 * @param {Record<string, object>} regionBlueprints — zone → blueprint root node
 * @param {Record<string, string>} panelZones
 * @param {Record<string, string>} panelKinds
 */
export function buildLayoutFromBlueprint(
    regionBlueprints = {},
    panelZones = {},
    panelKinds = {},
) {
    const regionSubtrees = {};
    for (const [zone, node] of Object.entries(regionBlueprints)) {
        const z = normalizeDockZone(zone);
        const subtree = buildBlueprintNode(node);
        if (subtree) regionSubtrees[z] = subtree;
    }

    if (Object.keys(regionSubtrees).length > 0) {
        return buildLayoutFromRegionSubtrees(regionSubtrees);
    }

    const panelDefs = Object.keys(panelZones).map((id) => ({
        id,
        zone: panelZones[id],
        panelKind: panelKinds[id],
    }));
    return buildLayoutFromPanels(panelDefs);
}
